import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AdvancedMarker,
    useMap,
    useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { Box, Typography } from '@mui/material';
import { theme } from '../../../../../shared/theme';
import { LatLng } from '../../../../types/itinerary';
import {
    pathMidpoint,
    geoMidpoint,
    haversineDistanceKm,
    estimateFlightDuration,
} from './ItineraryUtils';
import { TravelMode } from '../../../../enums/itinerary';

export type RouteInfo = {
    distanceKm: number;
    distanceText: string;
    durationText: string;
};

export type RouteProps = {
    origin: LatLng;
    destination: LatLng;
    travelMode: TravelMode;
};

type CachedRoute = {
    path: LatLng[];
    distanceText: string;
    durationText: string;
    distanceMeters: number;
};

// Cache for computed routes. RTK Query can't be used here because
// Route.computeRoutes() is a JS SDK method (not a REST call) that's only
// available after the Maps library loads via a React hook (useMapsLibrary).
const routeCache = new Map<string, CachedRoute>();

const cacheKey = (origin: LatLng, dest: LatLng, mode: string): string =>
    `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}|${dest.lat.toFixed(6)},${dest.lng.toFixed(6)}|${mode}`;

const Route = ({ origin, destination, travelMode }: RouteProps) => {
    const isFlight = travelMode === TravelMode.FLIGHT;
    const strokeColor = isFlight
        ? theme.palette.itineraryMap.flight
        : theme.palette.itineraryMap.drivingRoute;

    const map = useMap();
    const routesLibrary = useMapsLibrary('routes');

    // Cast needed: @vis.gl/react-google-maps types predate the Routes API but the runtime object supports it.
    const routeClass = (routesLibrary as any)?.Route;

    const polylineRef = useRef<google.maps.Polyline | null>(null);

    const [routeData, setRouteData] = useState<CachedRoute | null>(null);

    // Flight: geodesic line, no API call needed
    useEffect(() => {
        if (!isFlight) return;

        const km = haversineDistanceKm(origin, destination);
        const entry: CachedRoute = {
            path: [origin, destination],
            distanceText: `${Math.round(km).toLocaleString()} km`,
            durationText: estimateFlightDuration(origin, destination),
            distanceMeters: km * 1000,
        };
        setRouteData(entry);
    }, [isFlight, origin.lat, origin.lng, destination.lat, destination.lng]);

    // Driving/transit/etc: call Route.computeRoutes()
    useEffect(() => {
        if (isFlight || !map || !routeClass) return;

        let cancelled = false;
        const key = cacheKey(origin, destination, travelMode);
        const cached = routeCache.get(key);

        if (cached) {
            setRouteData(cached);
            return;
        }

        (async () => {
            const { routes } = await routeClass.computeRoutes({
                origin,
                destination,
                travelMode,
                fields: ['path', 'distanceMeters', 'localizedValues'],
            });

            if (cancelled || !routes?.length) return;

            const route = routes[0];
            const path: LatLng[] = (route.path ?? []).map(
                (p: google.maps.LatLng) => ({
                    lat: p.lat,
                    lng: p.lng,
                }),
            );
            const distanceText: string = route.localizedValues?.distance ?? '';
            const durationText: string =
                route.localizedValues?.staticDuration ?? '';
            const distanceMeters: number = route.distanceMeters ?? 0;

            const entry: CachedRoute = {
                path,
                distanceText,
                durationText,
                distanceMeters,
            };
            routeCache.set(key, entry);
            setRouteData(entry);
        })();

        return () => {
            cancelled = true;
        };
    }, [
        isFlight,
        map,
        routeClass,
        origin.lat,
        origin.lng,
        destination.lat,
        destination.lng,
        travelMode,
    ]);

    // Draw polyline
    useEffect(() => {
        if (!map || !routeData?.path.length) return;

        const polyline = new google.maps.Polyline({
            path: routeData.path,
            geodesic: isFlight,
            strokeColor,
            strokeOpacity: 0.7,
            strokeWeight: isFlight ? 2 : 4,
            ...(isFlight && {
                icons: [
                    {
                        icon: {
                            path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
                            scale: 3,
                        },
                        offset: '40%',
                    },
                ],
            }),
            map,
        });
        polylineRef.current = polyline;

        return () => {
            polyline.setMap(null);
            polylineRef.current = null;
        };
    }, [map, routeData, strokeColor, isFlight]);

    // Fit viewport to origin + destination
    useEffect(() => {
        if (!map || !routeData) return;

        const bounds = new google.maps.LatLngBounds();
        bounds.extend(origin);
        bounds.extend(destination);
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }, [
        map,
        routeData,
        origin.lat,
        origin.lng,
        destination.lat,
        destination.lng,
    ]);

    const labelPosition = useMemo(() => {
        if (!routeData) return null;
        return isFlight
            ? geoMidpoint(origin, destination)
            : pathMidpoint(routeData.path);
    }, [routeData, isFlight, origin, destination]);

    if (!labelPosition || !routeData) return null;

    return (
        <AdvancedMarker position={labelPosition} zIndex={0}>
            <Box
                sx={{
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.custom.grey}`,
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        lineHeight: 1.3,
                    }}
                >
                    {isFlight ? '✈ ' : '🚗 '}
                    {routeData.durationText}
                    {routeData.distanceText
                        ? ` · ${routeData.distanceText}`
                        : ''}
                </Typography>
            </Box>
        </AdvancedMarker>
    );
};

export default Route;
